/**
 * Recall lock state — "who is calling this patient right now".
 *
 *  • lock            (bool) — THE signal. true = someone is on it.
 *  • lock_by         (int)  — employee_id of the holder (may be absent on some feeds)
 *  • lock_timestamp  (ms)   — when it was claimed (informational only)
 *
 * ⚠️ **`lock` alone decides the tag.** No staleness/expiry window, no "was handled
 * before" state. The legacy (Bubble) system only ever writes `lock` — it does not
 * maintain `lock_timestamp`/`lock_by` — so deriving state from those made the tag
 * misfire on existing rows. Keep this dumb: lock === true → กำลังติดตาม, else nothing.
 *
 * Consequence to know about: a lock is only cleared by an explicit release (or the
 * holder opening another patient). A forgotten lock stays lit — but it is never a
 * dead end, because takeover is always allowed (รับต่อ in the popup).
 *
 * Rows arrive in two shapes (see Recall.jsx): a patient row (fields at top level)
 * or a recall row joined to patient (fields under `.patient`). Accessors read both.
 */

// tolerant accessors — top-level (patient row) OR nested under `.patient` (recall row)
export const lockOf = (r) => (r?.lock ?? r?.patient?.lock) === true
export const lockByOf = (r) => r?.lock_by ?? r?.patient?.lock_by ?? null
export const lockTsOf = (r) => Number(r?.lock_timestamp ?? r?.patient?.lock_timestamp ?? 0) || 0

/**
 * Derive the lock state for a row/patient.
 * @returns { status, active, mine, byOther, by, ts }
 *   status ∈ 'free' | 'mine' | 'other'
 *     free  — lock is false/absent
 *     mine  — locked AND lock_by is me
 *     other — locked, by someone else OR by an unknown holder (feeds that don't
 *             return lock_by can't attribute it; a neutral "กำลังติดตาม" is
 *             honest, where claiming it's yours would not be)
 */
export function lockState(r, meId) {
  const active = lockOf(r)
  const by = lockByOf(r)
  const ts = lockTsOf(r)
  const mine = active && meId != null && by != null && String(by) === String(meId)
  const byOther = active && !mine
  return { status: active ? (mine ? 'mine' : 'other') : 'free', active, mine, byOther, by, ts }
}

export const LOCK_META = {
  free: { label: 'ว่าง', cls: 'text-slate-500 bg-slate-50 border-slate-200', dot: 'bg-slate-300' },
  mine: { label: 'คุณกำลังติดตาม', cls: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  other: { label: 'กำลังติดตาม', cls: 'text-rose-700 bg-rose-50 border-rose-200', dot: 'bg-rose-500' },
}
